// src/api/client.js

import { toast } from 'react-toastify';
import { getAdminAcceptLanguage, isAdminPath } from '../utils/adminLanguage';

// Helper to get the API base URL based on environment
const getApiBaseUrl = () => {
  const currentHost = window.location.hostname;
  const isLocalhost = currentHost === 'localhost' || currentHost === '127.0.0.1';
  const host = isLocalhost ? 'localhost:8000' : currentHost;
  const protocol = window.location.protocol;
  return `${protocol}//${host}/api`;
};

const getAdminLanguageHeaders = () => (
  isAdminPath()
    ? { 'Accept-Language': getAdminAcceptLanguage() }
    : {}
);

const buildDefaultHeaders = (contentType = 'application/json') => {
  const headers = {
    'Accept': 'application/json',
    ...getAdminLanguageHeaders(),
  };

  if (contentType) {
    headers['Content-Type'] = contentType;
  }

  return headers;
};

const formatErrorDetail = (detail) => {
  if (!detail) {
    return null;
  }

  if (typeof detail === 'string') {
    return detail;
  }

  if (Array.isArray(detail)) {
    const messages = detail
      .map((item) => {
        if (typeof item === 'string') {
          return item;
        }

        if (item && typeof item === 'object') {
          const location = Array.isArray(item.loc) ? item.loc.join('.') : null;
          if (item.msg && location) {
            return `${location}: ${item.msg}`;
          }
          return item.msg || JSON.stringify(item);
        }

        return null;
      })
      .filter(Boolean);

    return messages.length > 0 ? messages.join('; ') : null;
  }

  if (typeof detail === 'object') {
    return detail.message || JSON.stringify(detail);
  }

  return String(detail);
};

// Response parser
const parseResponse = async (response) => {
  try {
    const data = await response.json().catch(() => null);
    if (response.status >= 200 && response.status < 300) {
      return { success: true, data };
    } else {
      let errorMsg = data?.error || formatErrorDetail(data?.detail) || 'An error occurred while processing your request';
      toast.error(errorMsg);
      return { 
        success: false, 
        data,
        error: errorMsg
      };
    }
  } catch (error) {
    let errorMsg = 'Failed to parse server response';
    toast.error(errorMsg);
    return {
      success: false,
      error: errorMsg
    };
  }
};

// Error handler
const handleApiError = (error) => {
  let errorMsg = null;
  if (error.name === 'TypeError' && error.message === 'Failed to fetch') {
    errorMsg = 'Unable to connect to the server. Please check your internet connection.'
  } else {
    errorMsg = error.message || 'An unexpected error occurred'
  }
  
  console.error('Global error:', errorMsg);
  toast.error(errorMsg);

  return {
    success: false,
    error: errorMsg
  };
};

// API client methods
export const api = {
  get: async (url) => {
    try {
      const baseUrl = getApiBaseUrl();
      const response = await fetch(`${baseUrl}${url}`, {
        method: 'GET',
        credentials: 'include',
        headers: buildDefaultHeaders(),
        mode: 'cors',
      });
      return parseResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  },
  post: async (url, data) => {
    try {
      const baseUrl = getApiBaseUrl();
      const response = await fetch(`${baseUrl}${url}`, {
        method: 'POST',
        headers: buildDefaultHeaders(),
        credentials: 'include',
        mode: 'cors',
        body: JSON.stringify(data),
      });
      return parseResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  },
  put: async (url, data) => {
    try {
      const baseUrl = getApiBaseUrl();
      const response = await fetch(`${baseUrl}${url}`, {
        method: 'PUT',
        headers: buildDefaultHeaders(),
        credentials: 'include',
        mode: 'cors',
        body: JSON.stringify(data),
      });
      return parseResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  },
  postForm: async (url, data) => {
    try {
      const baseUrl = getApiBaseUrl();
      const formData = new URLSearchParams();
      Object.entries(data).forEach(([key, value]) => {
        formData.append(key, value);
      });
      const response = await fetch(`${baseUrl}${url}`, {
        method: 'POST',
        headers: buildDefaultHeaders('application/x-www-form-urlencoded'),
        credentials: 'include',
        mode: 'cors',
        body: formData,
      });
      return parseResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  },
  postMultipart: async (url, formData) => {
    try {
      const baseUrl = getApiBaseUrl();
      const response = await fetch(`${baseUrl}${url}`, {
        method: 'POST',
        credentials: 'include',
        headers: getAdminLanguageHeaders(),
        mode: 'cors',
        body: formData,
      });
      return parseResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  },
  delete: async (url, data) => {
    try {
      const baseUrl = getApiBaseUrl();
      const response = await fetch(`${baseUrl}${url}`, {
        method: 'DELETE',
        headers: buildDefaultHeaders(),
        credentials: 'include',
        mode: 'cors',
        body: JSON.stringify(data),
      });
      return parseResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  },
  patch: async (url, data) => {
    try {
      const baseUrl = getApiBaseUrl();
      const response = await fetch(`${baseUrl}${url}`, {
        method: 'PATCH',
        headers: buildDefaultHeaders(),
        credentials: 'include',
        mode: 'cors',
        body: JSON.stringify(data),
      });
      return parseResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  },
}; 
